import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import { apiFetch } from '../api/fetch';

export interface AuthUser {
  id: string;
  nickname: string;
  email: string | null;
  avatarUrl: string | null;
  provider: 'kakao' | 'google';
}

interface MeResponse {
  user: AuthUser | null;
  termsUpdateRequired?: boolean;
}

export interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  termsUpdateRequired: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [termsUpdateRequired, setTermsUpdateRequired] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await apiFetch('/api/auth/me');
      if (!res.ok) {
        setUser(null);
        setTermsUpdateRequired(false);
        return;
      }
      const body = (await res.json()) as MeResponse;
      setUser(body.user);
      setTermsUpdateRequired(Boolean(body.user && body.termsUpdateRequired));
    } catch {
      setUser(null);
      setTermsUpdateRequired(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await apiFetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
    setTermsUpdateRequired(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <AuthContext.Provider value={{ user, loading, termsUpdateRequired, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
